import { useState } from 'react';
import { userExport } from "../../Redux/Slices/userAnalyticsSlice";
import PropTypes from "prop-types";
import { useDispatch } from 'react-redux';
import toast from 'react-hot-toast';
import ClipLoader from "react-spinners/ClipLoader";

const ExportFilterData = ({ isOpen, onClose, filterData }) => {
    const [userType, setUserType] = useState('all');
    const [selectedFields, setSelectedFields] = useState([]);
    const [loading, setLoading] = useState(false);
    const dispatch = useDispatch();

    const allFields = ['mobile', 'dob', 'address', 'education', 'business'];
    const parentFields = ['mobile', 'dob', 'address', 'education', 'business'];
    const childFields = ['Mother', 'Father', 'Wife', 'Sister', 'Brother', 'Daughter', 'Son'];

    const handleUserTypeChange = (e) => {
        const type = e.target.value;
        setUserType(type);
        setSelectedFields([]);
    };

    const handleFieldSelect = (field) => {
        setSelectedFields(prev => {
            if (prev.includes(field)) {
                return prev.filter(f => f !== field);
            } else {
                return [...prev, field];
            }
        });
    };

    const handleSelectAll = () => {
        const fields = userType === 'all' ? allFields :
            userType === 'onlyparent' ? parentFields :
                childFields;
        setSelectedFields(fields);
    };

    const getFieldsToDisplay = () => {
        switch (userType) {
            case 'all':
                return allFields;
            case 'onlyparent':
                return parentFields;
            case 'onlychild':
                return childFields;
            default:
                return allFields;
        }
    };

    const handleExportData = async () => {
        try {
            setLoading(true);
            const payload = {
                type: userType || "",
                selectedKeys: selectedFields || [],
                ...filterData || "",
                is_approved: true
            };
            const response = await dispatch(userExport(payload)).unwrap();
            if (response?.IsSuccess) {
                const a = document.createElement("a");
                a.style.display = "none";
                document.body.appendChild(a);
                a.href = response.Data;
                a.target = "_self";
                a.download = response.Data;
                a.click();
                window.URL.revokeObjectURL(response.Data);
                a.remove();
                toast.success('Data exported successfully');
                setLoading(false);
                onClose();
            }
        } catch (error) {
            toast.error('Failed to export data');
        } finally {
            setLoading(false);
        }
    }

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-2xl relative min-h-[400px]">
                {loading ? (
                    <div className="absolute inset-0 bg-white bg-opacity-70 flex items-center justify-center z-10 rounded-lg">
                        <div className="flex flex-col items-center">
                            <ClipLoader color="#286ab0" size={50} />
                            <span className="mt-3 text-gray-600 font-bold">Exporting Data...</span>
                        </div>
                    </div>
                ) : (
                    <>
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="text-xl font-semibold text-gray-800">Export</h2>
                            <button className="text-gray-500 hover:text-gray-700 font-medium" onClick={onClose}>
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        <div className="mb-6">
                            <label className="block text-gray-500 mb-2">User Type</label>
                            <div className="relative">
                                <select
                                    className="w-full p-2 border border-gray-300 rounded-md appearance-none"
                                    value={userType}
                                    onChange={handleUserTypeChange}
                                >
                                    <option value="all">All</option>
                                    <option value="onlyparent">Only Parent</option>
                                    <option value="onlychild">Only Child</option>
                                </select>
                            </div>
                        </div>

                        <div className="mb-6">
                            <label className="block text-gray-500 mb-2">Select Fields</label>
                            <div className="space-y-3">
                                <label className="w-1/2 md:w-1/3 lg:w-1/4 space-x-2">
                                    <input
                                        type="checkbox"
                                        className="form-checkbox rounded text-primary"
                                        checked={selectedFields.length === getFieldsToDisplay().length}
                                        onChange={handleSelectAll}
                                    />
                                    <span className="text-gray-700 font-medium">All</span>
                                </label>

                                <div className="flex flex-wrap -mx-2">
                                    {getFieldsToDisplay().map((field) => (
                                        <label key={field} className="w-1/2 md:w-1/3 lg:w-1/4 px-2 space-x-2">
                                            <input
                                                type="checkbox"
                                                className="form-checkbox rounded text-primary"
                                                checked={selectedFields.includes(field)}
                                                onChange={() => handleFieldSelect(field)}
                                            />
                                            <span className="text-gray-700 font-medium">{field}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex justify-center space-x-3">
                            <button className="btn_secondary" onClick={onClose}>
                                Cancel
                            </button>
                            <button className="btn py-2.5 md:py-3.5 px-8 md:px-12" onClick={handleExportData}>
                                Export
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

ExportFilterData.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    rowData: PropTypes.object,
    getUserAnalyticsListFun: PropTypes.func,
    filterData: PropTypes.object,
    children: PropTypes.any,
};

export default ExportFilterData;
